// Pääsuvõtmed in the browser: logging in with a passkey, adding one, and the
// person's own list. The ceremony is @simplewebauthn/browser's; the server half
// is server/auth/passkey.js.
//
// Same file in akt3/src/plugins and customer3/src/plugins — the apps build
// separately and share no source. Keep the three in step.
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser'

const call = async (method, url, body) => {
  let res
  try {
    res = await fetch(url, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined
    })
  } catch (e) {
    throw { message: 'Ühendus puudub või on liiga aeglane — proovi uuesti' }
  }
  let data = {}
  try { data = await res.json() } catch (e) { /* empty answer */ }
  if (!res.ok) throw { ...data, message: data.message || 'Serveri viga', status: res.status }
  return data
}

export const supported = () => {
  try { return browserSupportsWebAuthn() } catch (e) { return false }
}

// Whether this device can make its own key (Windows Hello with a PIN, Touch ID,
// the phone's lock). True when the browser cannot say: then it is left to try.
export const platformAvailable = async () => {
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch (e) {
    return true
  }
}

// Whether this computer can show the QR code for a phone: that needs Bluetooth.
// Without it the browser falls back to asking for a USB security key. False only
// when the browser says so outright; older browsers cannot say (Chrome 133+).
export const qrAvailable = async () => {
  try {
    const caps = await PublicKeyCredential.getClientCapabilities()
    return caps.hybridTransport !== false
  } catch (e) {
    return true
  }
}

// { user: { on, required }, client: { on, required } }; all off when unreadable.
export async function config () {
  try {
    return await call('GET', '/auth/passkey/config')
  } catch (e) {
    return { user: { on: false, required: false }, client: { on: false, required: false } }
  }
}

// The browser's own refusals, in words a person can act on.
function browserError (err) {
  const name = err && err.name
  if (name === 'NotAllowedError' || name === 'AbortError') return { message: 'Katkestatud või aeg sai otsa. Proovi uuesti.', cancelled: true }
  if (name === 'InvalidStateError') return { message: 'See seade on juba lisatud.' }
  if (name === 'SecurityError') return { message: 'See aadress ei luba pääsuvõtmeid.' }
  if (name === 'NotSupportedError') return { message: 'See seade ei toeta pääsuvõtmeid.' }
  return err && err.message && !err.name ? err : { message: 'Pääsuvõtmega ei õnnestunud. Proovi uuesti.' }
}

// kind: 'user' (intra, akt) or 'client' (customer portal). Resolves once logged in.
export async function login (kind) {
  const options = await call('POST', '/auth/passkey/login/options', { kind })
  let response
  try {
    response = await startAuthentication({ optionsJSON: options })
  } catch (err) {
    throw browserError(err)
  }
  try {
    return await call('POST', '/auth/passkey/login/verify', { kind, response })
  } catch (err) {
    // A key deleted here: ask the device to drop it, so it stops offering it.
    if (err.code === 'UNKNOWN_CREDENTIAL') signalUnknown(err)
    throw err
  }
}

// The WebAuthn Signal API (Chrome 132+, Safari 26+); elsewhere these do nothing.
function signalUnknown ({ rpId, credentialId }) {
  try {
    if (rpId && credentialId && window.PublicKeyCredential && PublicKeyCredential.signalUnknownCredential) {
      PublicKeyCredential.signalUnknownCredential({ rpId, credentialId }).catch(() => {})
    }
  } catch (e) { /* not supported */ }
}

// After the person's own list is read or changed: the keys this site still
// accepts for them, so the device forgets the ones deleted here.
export function signalAccepted (signal) {
  try {
    if (signal && window.PublicKeyCredential && PublicKeyCredential.signalAllAcceptedCredentials) {
      PublicKeyCredential.signalAllAcceptedCredentials({
        rpId: signal.rpId,
        userId: signal.userId,
        allAcceptedCredentialIds: signal.credentialIds
      }).catch(() => {})
    }
  } catch (e) { /* not supported */ }
}

// Adds this device for whoever is logged in. Resolves to { keys, mustAdd }.
// remote: the key is made on a phone through a QR code instead (from a PC).
export async function add (name, { remote = false } = {}) {
  const options = await call('POST', '/auth/passkey/register/options', remote ? { remote: true } : undefined)
  let response
  try {
    response = await startRegistration({ optionsJSON: options })
  } catch (err) {
    throw browserError(err)
  }
  return call('POST', '/auth/passkey/register/verify', { response, name })
}

export const mine = () => call('GET', '/auth/passkey/mine')
export const rename = (id, name) => call('PUT', '/auth/passkey/mine/' + id, { name })
export const remove = id => call('DELETE', '/auth/passkey/mine/' + id)

// Whether this is a phone or a tablet: there "Lisa telefon QR-koodiga" means nothing.
export const onPhone = () => /Android|iPhone|iPad|Mobile/.test(navigator.userAgent || '') ||
  (/Macintosh/.test(navigator.userAgent || '') && navigator.maxTouchPoints > 1)

// A name for this device the person can keep or change: "iPhone", "Windowsi arvuti".
export function deviceName () {
  const ua = navigator.userAgent || ''
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'iPad'
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'Android telefon' : 'Android tahvel'
  if (/Windows/.test(ua)) return 'Windowsi arvuti'
  if (/Macintosh/.test(ua)) return 'Mac'
  if (/Linux/.test(ua)) return 'Linuxi arvuti'
  return 'Pääsuvõti'
}

// dd.mm.yyyy, as everywhere in intra; '' for none.
export function day (value) {
  if (!value) return ''
  const d = new Date(String(value).replace(' ', 'T'))
  if (isNaN(d)) return ''
  return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + d.getFullYear()
}
